<template>
  <draggable
    :model-value="draggableModel"
    v-bind="options"
    item-key="0"
    class="flex flex-col gap-0.5"
    @add="handleAdd"
    @update="handleUpdate"
  >
    <template #item="{ element: [key, item] }: { element: [string, PivotTableField] }">
      <PivotField
        :caption="getFieldCaption(key, captions?.[key])"
        :enabled="item.enabled"
        :readonly
        @toggle="enabled => handleToggle(key, enabled)"
      />
    </template>
  </draggable>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import draggable from 'vuedraggable'
import { cloneDeep, orderBy } from 'lodash'

import { PivotTableCaptions, PivotTableConfig, PivotTableField, PivotTableGroup } from '@types'

import { CAPTIONS_PROVIDE_KEY } from '@/components/DS/patterns/Pivot/utils/const'

import PivotField from './primitives/PivotField.vue'
import { getFieldCaption } from './utils'

type Props = {
  /** vuedraggable group name — matching groups can drag fields between each other (e.g. rows <-> columns). */
  group?: string
  section: PivotTableGroup
  readonly?: boolean
}

const { group, section, readonly } = defineProps<Props>()

const modelValue = defineModel<PivotTableConfig>()

const captions = inject(CAPTIONS_PROVIDE_KEY, computed<PivotTableCaptions>(() => ({})))

const draggableModel = computed(() => {
  if (!modelValue.value) return []
  return orderBy(
    Object.entries(modelValue.value.fields || {}).filter(
      ([, field]) => field.group === section,
    ),
    item => item[1].index,
  )
})

const options = computed(() => ({
  animation: 200,
  disabled: readonly,
  handle: '[data-handle]',
  itemKey: (element: [string, PivotTableField]) => element[0],
  // A plain string lets Rows/Columns drag into each other; omitted (Values) keeps it isolated.
  group,
}))

// `fields`'s `index` is scoped per-section, so moving a field re-indexes only its section's siblings.
const handleAdd = (event: any) => {
  if (!modelValue.value?.fields) return
  const fields = cloneDeep(modelValue.value?.fields)
  const key = event.item._underlying_vm_[0]
  fields[key].group = section
  fields[key].index = event.newIndex
  draggableModel.value.forEach(([key], index) => {
    fields[key].index = index + Number(index >= event.newIndex)
  })
  modelValue.value = {
    ...modelValue.value,
    fields,
  }
}

const handleUpdate = (event: any) => {
  if (event.pullMode || !modelValue.value?.fields) return
  const fields = cloneDeep(modelValue.value?.fields)
  const key = event.item._underlying_vm_[0]
  const oldIndex = event.oldIndex
  const newIndex = event.newIndex
  fields[key].index = newIndex
  const reverse = newIndex < oldIndex
  draggableModel.value.forEach(([fieldKey], index) => {
    if (key === fieldKey) return
    let diff = 0
    if (reverse && index < oldIndex) {
      diff = Number(index >= event.newIndex)
    } else if (index > oldIndex) {
      diff = index > event.newIndex ? +1 : -1
    }
    fields[fieldKey].index = index + diff
  })
  modelValue.value = {
    ...modelValue.value,
    fields,
  }
}

const handleToggle = (key: string, enabled: boolean) => {
  if (!modelValue.value?.fields) return
  const fields = cloneDeep(modelValue.value?.fields)
  fields[key].enabled = enabled
  modelValue.value = {
    ...modelValue.value,
    fields,
  }
}
</script>
